import { useContext, useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { getAllOffers } from 'selectors/offerSelectors'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import {
  fetchCharterBundleFlightPrice,
  resolveCharterBundle,
  type CharterFlightBundleSelection,
} from 'tripPlanner/utils/charterBundleHydration'

export interface CharterCartFlightPriceInput {
  tripItemId: number
  offer: App.HotelOffer | App.HotelOfferSummary
  charterFlightBundle: CharterFlightBundleSelection
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  region: string
  currency: string
  email?: string
}

export interface CharterCartFlightPrice {
  flightPrice: number | undefined
  loading: boolean
  /**
   * The price probe ran and couldn't price the bundle's flight for the selected
   * dates (checkout would hard-block). Distinct from `loading` and from "no
   * probe yet" — consumers use this to show the item as unavailable rather than
   * a misleading hotel-only total.
   */
  unavailable: boolean
}

/**
 * Build a charter flight-price query input from a cart/itinerary item's raw
 * fields, returning null when the item isn't a priceable charter bundle. Detection
 * + fallbacks run through the shared `resolveCharterBundle` so the displayed price
 * uses the exact same eligibility (live offer campaign tag + region) and the exact
 * same origin/fare/occupancy defaults as the checkout charge — they can't diverge.
 */
export function buildCharterCartFlightPriceInput(params: {
  tripItemId: number
  offer: App.AnyOffer | undefined
  isMaldivesCharterEnabled: boolean
  charterFlightBundle: CharterFlightBundleSelection | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  occupancies: Array<App.Occupants> | undefined
  region: string
  currency: string
  email?: string
}): CharterCartFlightPriceInput | null {
  const { offer, isMaldivesCharterEnabled, charterFlightBundle, checkIn, checkOut, occupancies } = params
  if (!checkIn || !checkOut) return null

  const resolved = resolveCharterBundle({
    offer,
    isMaldivesCharterEnabled,
    persistedBundle: charterFlightBundle,
    occupancies,
  })
  if (!resolved) return null

  return {
    tripItemId: params.tripItemId,
    offer: resolved.offer,
    charterFlightBundle: resolved.charterFlightBundle,
    checkIn,
    checkOut,
    occupancies: resolved.occupancies,
    region: params.region,
    currency: params.currency,
    email: params.email,
  }
}

const FIVE_MINUTES = 5 * 60 * 1000

/**
 * Live charter-bundle flight price per charter cart item, so the cart/itinerary
 * total reflects hotel + flight (like the offer page) instead of hotel-only.
 * Batched with useQueries; each query reuses fetchCharterBundleFlightPrice so the
 * displayed price matches what checkout builds. Keyed on the selection so it
 * refetches when origin/fare/dates/occupancy change.
 */
export function useCharterCartFlightPrices(
  inputs: Array<CharterCartFlightPriceInput>,
): Map<number, CharterCartFlightPrice> {
  const results = useQueries({
    queries: inputs.map((input) => ({
      queryKey: [
        'charterCartFlightPrice',
        input.tripItemId,
        input.offer.id,
        input.charterFlightBundle.charterBundleRoute,
        input.charterFlightBundle.originAirportCode,
        input.charterFlightBundle.fareClass,
        input.checkIn,
        input.checkOut,
        input.region,
        input.currency,
        input.email,
        input.occupancies,
      ],
      queryFn: async(): Promise<number | null> => {
        const result = await fetchCharterBundleFlightPrice({
          offer: input.offer,
          charterFlightBundle: input.charterFlightBundle,
          checkIn: input.checkIn,
          checkOut: input.checkOut,
          occupancies: input.occupancies,
          region: input.region,
          currency: input.currency,
          email: input.email,
        })
        return result.ok ? result.flightPrice : null
      },
      enabled: !!input.checkIn && !!input.checkOut && input.occupancies.length > 0,
      staleTime: FIVE_MINUTES,
    })),
  })

  const priceSignature = inputs
    .map((input, i) => `${input.tripItemId}:${results[i]?.isLoading ? 'L' : results[i]?.isError ? 'E' : results[i]?.data}`)
    .join('|')

  // priceSignature captures the inputs + query data/loading/error, so the map
  // identity is stable until they change — keying off it (rather than the fresh
  // inputs/results arrays useQueries returns each render) is deliberate, so the
  // exhaustive-deps diagnostics on inputs/results/priceSignature are suppressed.
  /* oxlint-disable react-hooks/exhaustive-deps */
  const charterCartFlightPrices = useMemo(() => new Map(inputs.map((input, i) => [
    input.tripItemId,
    {
      flightPrice: results[i]?.data ?? undefined,
      loading: !!results[i]?.isLoading,
      unavailable: results[i]?.data === null || !!results[i]?.isError,
    },
  ])), [priceSignature])
  /* oxlint-enable react-hooks/exhaustive-deps */
  return charterCartFlightPrices
}

export interface CharterItemFlightPrice extends CharterCartFlightPrice {
  /** True when the item resolved to a priceable charter bundle (offer detected + region on + dates present). */
  isCharterBundle: boolean
}

/**
 * Single-item charter flight price for surfaces that render one accommodation
 * item at a time (e.g. the itinerary tile). Owns the offer selection, input
 * construction and query execution so the consuming component stays presentational
 * and shares the exact same pricing path as the batched cart pricing.
 */
export function useCharterItemFlightPrice(params: {
  tripItemId: number
  offerId: string | undefined
  charterFlightBundle: CharterFlightBundleSelection | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  occupancies: Array<App.Occupants> | undefined
}): CharterItemFlightPrice {
  const { currentRegionCode } = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)
  const { offer, email } = useAppSelector((state) => ({
    offer: params.offerId ? getAllOffers(state)[params.offerId] : undefined,
    email: state.auth?.account?.email,
  }), shallowEqual)

  const inputs = useMemo<Array<CharterCartFlightPriceInput>>(() => {
    const input = buildCharterCartFlightPriceInput({
      tripItemId: params.tripItemId,
      offer,
      isMaldivesCharterEnabled,
      charterFlightBundle: params.charterFlightBundle,
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      occupancies: params.occupancies,
      region: currentRegionCode,
      currency,
      email,
    })
    return input ? [input] : []
  }, [params.tripItemId, params.charterFlightBundle, params.checkIn, params.checkOut, params.occupancies, offer, isMaldivesCharterEnabled, currentRegionCode, currency, email])

  const result = useCharterCartFlightPrices(inputs).get(params.tripItemId)
  return {
    flightPrice: result?.flightPrice,
    loading: !!result?.loading,
    unavailable: !!result?.unavailable,
    isCharterBundle: inputs.length > 0,
  }
}
